import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { LogIn, Mail, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { authSchema } from "@/lib/validation";
import { SEO } from "@/lib/seo";

const fieldClass = "w-full rounded-xl border border-border bg-card px-4 py-3 text-foreground outline-none transition placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/25";
const labelClass = "mb-2 block text-sm font-bold text-foreground";

const Auth = () => {
  const { userId, loading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=signup") || hash.includes("type=recovery") || hash.includes("access_token")) {
      toast.success(t("auth.verifySuccess"));
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [t]);

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">…</div>;
  if (userId) return <Navigate to={from} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = authSchema(mode).safeParse({ email, password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setBusy(true);
    setVerifyMessage(null);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/auth` },
        });
        if (error) throw error;
        if (data.user && !data.session) {
          setVerifyMessage(t("auth.checkEmail"));
        }
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setVerifyMessage(t("auth.resetEmailSent"));
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          if (error.message === "Email not confirmed") {
            throw new Error(t("auth.emailNotConfirmed"));
          }
          throw error;
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const showPassword = mode !== "forgot";
  const title = mode === "forgot" ? t("auth.resetPassword") : t("auth.title");

  return (
    <section className="min-h-screen bg-hero-gradient ambient-grid px-4 py-12 sm:px-6 lg:px-8">
      <SEO title={t("seo.auth.title")} description={t("seo.auth.description")} path="/auth" noindex />
      <div className="mx-auto max-w-md">
        <h1 className="mb-6 text-center text-3xl font-black">{title}</h1>
        <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-background/90 p-6 shadow-soft backdrop-blur">
          <label className="block">
            <span className={labelClass}>{t("auth.email")}</span>
            <input className={fieldClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </label>
          {showPassword && (
            <label className="mt-4 block">
              <span className={labelClass}>{t("auth.password")}</span>
              <input className={fieldClass} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete={mode === "signup" ? "new-password" : "current-password"} />
            </label>
          )}

          {verifyMessage && (
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-success/40 bg-success/15 px-4 py-3 text-success">
              <Mail className="mt-0.5 h-5 w-5 shrink-0" />
              <p className="text-sm font-bold">{verifyMessage}</p>
            </div>
          )}

          <button disabled={busy} type="submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-black text-primary-foreground transition hover:scale-[1.01]">
            {mode === "signin" ? (<><LogIn className="h-4 w-4" /> {t("auth.signIn")}</>)
              : mode === "signup" ? (<><UserPlus className="h-4 w-4" /> {t("auth.signUp")}</>)
              : (<><Mail className="h-4 w-4" /> {t("auth.sendResetLink")}</>)}
          </button>

          {mode === "signin" && (
            <button type="button" onClick={() => { setMode("forgot"); setVerifyMessage(null); }} className="mt-3 w-full text-center text-sm font-bold text-muted-foreground hover:text-primary hover:underline">
              {t("auth.forgotPassword")}
            </button>
          )}

          <button type="button" onClick={() => {
            setMode(mode === "signin" ? "signup" : mode === "signup" ? "signin" : "signin");
            setVerifyMessage(null);
          }} className="mt-4 w-full text-center text-sm font-bold text-primary hover:underline">
            {mode === "signin" ? t("auth.toggleToSignup") : mode === "signup" ? t("auth.toggleToSignin") : t("auth.backToSignIn")}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground"><Link to="/" className="hover:text-foreground">← {t("auth.backToHome")}</Link></p>
      </div>
    </section>
  );
};

export default Auth;
